import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "~/lib/utils"
import { RemoteImg } from "~/components/ui/remote-img"

const avatarVariants = cva(
  "group/avatar relative flex shrink-0 overflow-hidden select-none",
  {
    variants: {
      variant: {
        user: "size-avatar rounded-full",
        group: "size-avatar rounded-lg",
        "group-post": "size-12 rounded-lg",
        "group-sm": "size-8 rounded-lg",
        profile: "w-full aspect-square text-8xl",
      },
    },
    defaultVariants: {
      variant: "user",
    },
  }
)

function AvatarContainer({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & VariantProps<typeof avatarVariants>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(avatarVariants({ variant }), className)}
      {...props}
    />
  )
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image data-slot="avatar-image" className={cn("aspect-square size-full", className)} {...props} />
  )
}

function AvatarFallback({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "bg-muted text-muted-foreground flex size-full items-center justify-center rounded-[inherit] text-sm",
        className,
      )}
      {...props}
    />
  )
}

function AvatarBadge({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="avatar-badge"
      className={cn(
        "bg-primary text-primary-foreground ring-background absolute right-0 bottom-0 z-10 inline-flex size-2.5 items-center justify-center rounded-full ring-2 select-none [&>svg]:size-2",
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group"
      className={cn(
        "*:data-[slot=avatar]:ring-background group/avatar-group flex -space-x-2 *:data-[slot=avatar]:ring-2",
        className,
      )}
      {...props}
    />
  )
}

function AvatarGroupCount({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="avatar-group-count"
      className={cn(
        "bg-muted text-muted-foreground ring-background relative flex size-avatar shrink-0 items-center justify-center rounded-full text-sm ring-2 [&>svg]:size-4",
        className,
      )}
      {...props}
    />
  )
}

interface AvatarProps {
  url?: string
  name?: string
  className?: string
  variant?: VariantProps<typeof avatarVariants>["variant"]
}

function Avatar({ url, name, className, variant }: AvatarProps) {
  return (
    <AvatarContainer className={className} variant={variant}>
      {url ? (
        <RemoteImg src={url} alt={name ?? ""} className="absolute inset-0 size-full object-cover" />
      ) : (
        <AvatarFallback className={cn(avatarVariants({ variant }), className)}>
          {name?.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
        </AvatarFallback>
      )}
    </AvatarContainer>
  )
}

export { Avatar, AvatarBadge, AvatarContainer, AvatarFallback, AvatarGroup, AvatarGroupCount, AvatarImage }
